'use client';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination } from 'swiper/modules';
import { alpha, IconButton, styled } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icons } from '@peerless/ui-assets/icons';
import { useId, useMemo } from 'react';
import { AutoplayOptions, PaginationOptions, SwiperOptions } from 'swiper/types';
import { NavigationOptions, TransitionOptions } from '@peerless/data-access/strapi';

import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

interface SliderProps {
  minHeight?: string;
  autoplay?: boolean | null;
  autoplaySpeed?: number;
  navigation?: NavigationOptions;
  infiniteLoop?: boolean;
  transition?: TransitionOptions;
  transitionSpeed?: number;
  items: React.ReactElement[];
  breakpoints?: {
    [width: number]: SwiperOptions;
    [ratio: string]: SwiperOptions;
  };
}

const MIN_HEIGHT_DEFAULT = 50;
const TRANSITION_SPEED_DEFAULT = 500;
const AUTOPLAY_SPEED_DEFAULT = 3;
const BREAKPOINT_DEFAULTS = {
  640: {
    slidesPerView: 1,
  },
  768: {
    slidesPerView: 2,
  },
  1024: {
    slidesPerView: 3,
  },
};

export default function Slider({
  minHeight,
  autoplay,
  autoplaySpeed,
  navigation = 'Dots_and_Arrows',
  infiniteLoop,
  transition,
  transitionSpeed,
  items,
  breakpoints,
}: SliderProps) {
  const autoplayOptions = useMemo(() => {
    if (!autoplay) return undefined;
    const _autoplaySpeed = autoplaySpeed ?? AUTOPLAY_SPEED_DEFAULT;
    const speed = _autoplaySpeed > 0 ? (11 - _autoplaySpeed) * 1000 : 3000;
    return { delay: speed } as AutoplayOptions;
  }, [autoplay, autoplaySpeed]);
  const id = useId();
  const paginationOptions = useMemo(() => {
    if (['Dots_and_Arrows', 'Dots'].includes(navigation)) {
      return {
        el: '.swiper-pagination',
        clickable: true,
      } as PaginationOptions;
    }
    return undefined;
  }, [navigation]);

  const navigationOptions = useMemo(() => {
    if (['Dots_and_Arrows', 'Arrows'].includes(navigation))
      return {
        nextEl: '.swiper-button-next',
        prevEl: '.swiper-button-prev',
      };
    return undefined;
  }, [navigation]);

  return (
    <ImageSwiper
      // install Swiper modules
      modules={[Navigation, Pagination, Autoplay, EffectFade]}
      spaceBetween={16}
      navigation={navigationOptions}
      autoplay={autoplayOptions}
      loop={infiniteLoop}
      pagination={paginationOptions}
      speed={transitionSpeed ?? TRANSITION_SPEED_DEFAULT}
      effect={transition?.toLowerCase()}
      sx={{ minHeight: minHeight ?? MIN_HEIGHT_DEFAULT }}
      breakpoints={breakpoints ?? BREAKPOINT_DEFAULTS}
    >
      {items.map((item, i) => {
        return <SwiperSlide key={`${id}-${i}`}>{item}</SwiperSlide>;
      })}
      {navigationOptions && (
        <>
          <IconButton className="swiper-button swiper-button-next">
            <FontAwesomeIcon fixedWidth icon={icons.chevronRight} />
          </IconButton>
          <IconButton className="swiper-button swiper-button-prev">
            <FontAwesomeIcon fixedWidth icon={icons.chevronLeft} />
          </IconButton>
        </>
      )}

      <div className="swiper-pagination"></div>
    </ImageSwiper>
  );
}

const ImageSwiper = styled(Swiper)(({ theme }) => ({
  width: '100%',
  marginTop: 0,
  marginBottom: 0,
  '& .MuiPaper-root.video-slider-card': {
    backgroundColor: 'transparent',
  },

  '& .swiper-button': {
    position: 'absolute',
    width: 50,
    height: 50,
    marginTop: -25,
    backgroundColor: alpha(theme.palette.common.white, 0.25),
    display: 'flex',

    ':hover': {
      backgroundColor: theme.palette.common.white,
    },

    ':after': {
      content: '""',
    },

    '&.swiper-button-next': {
      left: 'auto',
      right: theme.spacing(1),
    },

    '&.swiper-button-prev': {
      left: theme.spacing(1),
      right: 'auto',
    },
  },

  '& .swiper-wrapper': {},

  '& .swiper-slide': {
    display: 'flex',
    height: 'auto',

    '& .MuiCard-root': {
      height: 'auto',
      width: '100%',
    },
  },
  '&. swiper-pagination': {
    position: 'absolute',
    textAlign: 'center',
    zIndex: 10,
  },
  '& .swiper-pagination-clickable': {
    position: 'absolute',
    textAlign: 'center',
    zIndex: 10,

    '& .swiper-pagination-bullet': {
      width: '12px',
      height: '12px',
      display: 'inline-block',
      borderRadius: '50%',
      margin: '0 4px',
      lineHeight: '12px',
      color: 'black',
      background: 'gray',
    },

    '& .swiper-pagination-bullet-active': {
      opacity: 1,
      color: 'white',
      background: theme.palette.primary.main,
    },
    '& .swiper-pagination-bullets': {
      '&.swiper-pagination-horizontal': {
        bottom: '8px',
        top: 'auto',
        left: 0,
        width: '100%',
      },
    },
  },
}));
